import React, { useCallback } from 'react'
import { useEnterpriseAccountSetDisabledMutation } from 'domains/account-management/api'
import { useNotifications } from 'legacy/common/utils/notifications/notifications.hooks'
import { DefaultNotificationContent } from 'legacy/components/Notifications/NotificationContents/DefaultNotificationContent'
import { useDomainMessages } from 'domains/account-management/utils/domain/intl'

export interface UseDisableAccountOptions {
  accountCanonicalId: string
  showNotifications?: boolean
  disable?: boolean
  refetchAccounts?: () => void
}

export const useDisableAccount = ({
  accountCanonicalId,
  showNotifications = true,
  disable = true,
  refetchAccounts,
}: UseDisableAccountOptions) => {
  const messages = useDomainMessages({
    successHeader: disable
      ? 'accountManagementDomain.disableAccount.success.header'
      : 'accountManagementDomain.enableAccount.success.header',
    errorHeader: disable
      ? 'accountManagementDomain.disableAccount.error.header'
      : 'accountManagementDomain.enableAccount.error.header',
    errorContent: disable
      ? 'accountManagementDomain.disableAccount.error.content'
      : 'accountManagementDomain.enableAccount.error.content',
  })

  const { notifyError, notify } = useNotifications()
  const [disableAccountBase, { loading }] = useEnterpriseAccountSetDisabledMutation({
    variables: { accountCanonicalId, value: disable },
  })

  const disableAccount = useCallback(async () => {
    const errorNotificationContent = (
      <DefaultNotificationContent header={messages.errorHeader} content={messages.errorContent} />
    )
    const successNotificationContent = (
      <DefaultNotificationContent header={messages.successHeader} content="" />
    )

    return disableAccountBase()
      .then(result => {
        refetchAccounts()

        if (!showNotifications) return result
        if (result.errors) return notifyError(errorNotificationContent, 'modern')

        return notify(successNotificationContent, 'modern')
      })
      .catch(err => {
        if (!showNotifications) throw err
        notifyError(errorNotificationContent, 'modern')
      })
  }, [disableAccountBase, notify, notifyError, messages, showNotifications, refetchAccounts])

  return { loading, disableAccount }
}
